import React, { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, Inbox, MapPin, AlertTriangle, ChevronRight, Building2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TableToolbar from "@/components/shared/TableToolbar";
import TablePagination from "@/components/shared/TablePagination";
import { adminProvidersApi } from "@/lib/api/adminProviders";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/lib/roles";
import { formatApiErrorDetail } from "@/lib/api";
import CreateProviderDialog from "@/components/admin/CreateProviderDialog";
import ProviderDetailSheet from "@/components/admin/ProviderDetailSheet";

function useDebounce(value, delay = 300) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

function KycBadge({ status, t }) {
    const tone = status === "APPROVED" ? "border-success/40 text-success bg-success/5"
              : status === "PENDING"  ? "border-warning/40 text-warning bg-warning/5"
              : status === "REJECTED" ? "border-destructive/40 text-destructive bg-destructive/5"
              : "border-border text-muted-foreground";
    return (
        <Badge variant="outline" className={tone}>
            {t(`admin.providers.kyc.${status}`)}
        </Badge>
    );
}

export default function ProvidersPage() {
    const { t } = useI18n();
    const { user } = useAuth();
    const canEdit = [ROLES.SUPER_ADMIN, ROLES.OPS_ADMIN].includes(user?.role);

    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [sortValue, setSortValue] = useState("created_desc");
    const [dateRange, setDateRange] = useState({ from: "", to: "" });
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const debounced = useDebounce(query, 300);

    const onDelete = async () => {
        if (!deleting) return;
        setDeleteSubmitting(true);
        try {
            await adminProvidersApi.delete(deleting.id);
            setItems((prev) => prev?.filter((p) => p.id !== deleting.id) || prev);
            setTotal((p) => Math.max(0, p - 1));
            toast.success(t("admin.providers.deleted", { name: deleting.corporate_name }));
            setDeleting(null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setDeleteSubmitting(false);
        }
    };

    const load = useCallback(async () => {
        try {
            const params = {
                q: debounced || undefined,
                kyc_status: filterValues.kyc_status || undefined,
                type: filterValues.type || undefined,
                visible: filterValues.visible || undefined,
                date_from: dateRange.from || undefined,
                date_to: dateRange.to || undefined,
                sort: sortValue || undefined,
                limit: pageSize,
                skip: (page - 1) * pageSize,
            };
            const res = await adminProvidersApi.list(params);
            setItems(res.items);
            setTotal(res.total);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    }, [debounced, filterValues, dateRange.from, dateRange.to, sortValue, pageSize, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [debounced, filterValues, dateRange.from, dateRange.to, sortValue, pageSize]);

    const onCreated = (row) => {
        toast.success(t("admin.providers.created"));
        setItems((prev) => [row, ...(prev || [])]);
        setTotal((p) => p + 1);
    };

    const onProviderChanged = (updated) => {
        setItems((prev) => prev?.map((p) => (p.id === updated.id ? updated : p)) || prev);
    };

    return (
        <div className="max-w-7xl space-y-6 fade-up" data-testid="page-admin-providers">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("admin.providers.title")}</h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{t("admin.providers.subtitle")}</p>
                </div>
                {canEdit && (
                    <Button onClick={() => setCreateOpen(true)} data-testid="add-provider-button">
                        <Plus className="h-4 w-4 me-1" />
                        {t("admin.providers.add")}
                    </Button>
                )}
            </header>

            <TableToolbar
                search={query}
                onSearchChange={setQuery}
                searchPlaceholder={t("admin.providers.searchPlaceholder")}
                filters={[
                    {
                        key: "kyc_status",
                        label: t("admin.providers.cols.kyc"),
                        options: [
                            { value: "DRAFT", label: t("admin.providers.kyc.DRAFT") },
                            { value: "PENDING", label: t("admin.providers.kyc.PENDING") },
                            { value: "APPROVED", label: t("admin.providers.kyc.APPROVED") },
                            { value: "REJECTED", label: t("admin.providers.kyc.REJECTED") },
                        ],
                    },
                    {
                        key: "type",
                        label: t("admin.providers.cols.type"),
                        options: [
                            { value: "CLINIC", label: t("provider.types.CLINIC") },
                            { value: "HOSPITAL", label: t("provider.types.HOSPITAL") },
                            { value: "PHARMACY", label: t("provider.types.PHARMACY") },
                            { value: "LAB", label: t("provider.types.LAB") },
                            { value: "RADIOLOGY_CENTER", label: t("provider.types.RADIOLOGY_CENTER") },
                            { value: "OTHER", label: t("provider.types.OTHER") },
                        ],
                    },
                    {
                        key: "visible",
                        label: t("admin.providers.cols.visibility"),
                        options: [
                            { value: "true", label: t("admin.providers.visible") },
                            { value: "false", label: t("admin.providers.hidden") },
                        ],
                    },
                ]}
                filterValues={filterValues}
                onFilterChange={(k, v) => setFilterValues((p) => ({ ...p, [k]: v }))}
                onClearFilters={() => setFilterValues({})}
                sortOptions={[
                    { value: "created_desc", label: t("table.sort.createdDesc") },
                    { value: "created_asc", label: t("table.sort.createdAsc") },
                    { value: "name_asc", label: t("table.sort.nameAsc") },
                    { value: "name_desc", label: t("table.sort.nameDesc") },
                ]}
                sortValue={sortValue}
                onSortChange={setSortValue}
                showDates
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
            />

            {items === null && (
                <div className="surface-card p-12 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            )}

            {items && items.length === 0 && (
                <div className="surface-card p-10 text-center" data-testid="providers-empty">
                    <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">{t("admin.providers.empty")}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("admin.providers.emptyHint")}</p>
                </div>
            )}

            {items && items.length > 0 && (
                <div className="surface-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="providers-table">
                            <thead className="bg-muted/30">
                                <tr>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.provider")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.type")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.kyc")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.branches")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.admin")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.visibility")}</th>
                                    <th className="px-5 py-3 font-semibold text-end">{t("admin.providers.cols.actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => {
                                    const expSoon = row.license_days_to_expiry !== null
                                        && row.license_days_to_expiry !== undefined
                                        && row.license_days_to_expiry <= 30;
                                    return (
                                        <tr
                                            key={row.id}
                                            className="border-t border-border hover:bg-muted/20 cursor-pointer"
                                            onClick={() => setActiveId(row.id)}
                                            data-testid={`provider-row-${row.id}`}
                                        >
                                            <td className="px-5 py-3">
                                                <div className="flex items-start gap-2.5">
                                                    <div className="icon-tile h-9 w-9 shrink-0 overflow-hidden">
                                                        {row.logo_url
                                                            ? <img src={row.logo_url} alt="" className="h-full w-full object-cover" />
                                                            : <Building2 className="h-4 w-4" />}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-semibold truncate">{row.corporate_name}</div>
                                                        {row.category_ids?.length > 0 && (
                                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                                {t("admin.providers.linkedCategories", { n: row.category_ids.length })}
                                                            </div>
                                                        )}
                                                        {expSoon && (
                                                            <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-destructive">
                                                                <AlertTriangle className="h-3 w-3" />
                                                                {t("admin.providers.licenseExpiring", { days: row.license_days_to_expiry })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3">
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                                                    {t(`provider.types.${row.type}`)}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3">
                                                <KycBadge status={row.kyc_status} t={t} />
                                            </td>
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-1 text-sm">
                                                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                                                    {row.branch_count || 0}
                                                </div>
                                                {row.neighborhoods?.length > 0 && (
                                                    <div className="text-xs text-muted-foreground mt-0.5 max-w-[200px] truncate">
                                                        {row.neighborhoods.slice(0, 3).join(" · ")}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-5 py-3">
                                                {row.admin ? (
                                                    <>
                                                        <div className="text-sm">{row.admin.full_name}</div>
                                                        <div className="text-xs text-muted-foreground font-mono">{row.admin.email}</div>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground italic">
                                                        {t("admin.providers.noAdmin")}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3">
                                                {row.is_visible ? (
                                                    <Badge variant="outline" className="border-success/40 text-success">
                                                        {t("admin.providers.visible")}
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary">{t("admin.providers.hidden")}</Badge>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-end">
                                                <div className="inline-flex items-center gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={(e) => { e.stopPropagation(); setActiveId(row.id); }}
                                                        data-testid={`provider-view-${row.id}`}
                                                    >
                                                        {t("admin.providers.view")}
                                                        <ChevronRight className="h-4 w-4 ms-1" />
                                                    </Button>
                                                    {canEdit && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={(e) => { e.stopPropagation(); setDeleting(row); }}
                                                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                            data-testid={`provider-delete-${row.id}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <TablePagination
                        total={total}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                    />
                </div>
            )}

            <CreateProviderDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={onCreated}
            />
            <ProviderDetailSheet
                open={!!activeId}
                onOpenChange={(o) => !o && setActiveId(null)}
                providerId={activeId}
                onProviderChanged={onProviderChanged}
            />

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && !deleteSubmitting && setDeleting(null)}>
                <AlertDialogContent data-testid="provider-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("admin.providers.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("admin.providers.confirmDeleteBody", { name: deleting?.corporate_name || "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteSubmitting}>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            disabled={deleteSubmitting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="provider-delete-confirm-button"
                        >
                            {deleteSubmitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t("common.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
